import type {
  ImdgSubstanceDetail,
  AdditionalRequirements,
  OptionalDeclarationParameters,
  SubstanceCore,
  Nullish,
} from '@/api/types';

export const hazcheckSubstanceUrl = (substanceId: string) =>
  `https://validate.hazcheck.com/substances/${substanceId}`;

export function getHazardClass(substance: Nullish<ImdgSubstanceDetail>): string {
  return substance?.class?.[0] ?? '';
}

export function getProperShippingName(substance: Nullish<ImdgSubstanceDetail>): string {
  return substance?.properShippingNameIncludingQDT || substance?.properShippingName || '';
}

export function getPackingGroup(substance: Nullish<ImdgSubstanceDetail>): string {
  return substance?.packingGroup ?? '';
}

export function getRequirements(
  substance: Nullish<ImdgSubstanceDetail>
): AdditionalRequirements | undefined {
  return substance?.additionalRequirements;
}

export function getOptionalParams(
  substance: Nullish<ImdgSubstanceDetail>
): OptionalDeclarationParameters | undefined {
  return substance?.optionalDeclarationParameters;
}

export function extractSubstanceCore(substance: Nullish<ImdgSubstanceDetail>): SubstanceCore {
  if (!substance) {
    return {
      unNumber: '',
      properShippingName: '',
      hazardClass: '',
      packingGroup: '',
      technicalName: '',
    };
  }

  return {
    unNumber: substance.unNumber ?? '',
    properShippingName: getProperShippingName(substance),
    hazardClass: getHazardClass(substance),
    packingGroup: getPackingGroup(substance),
    // ImdgSubstanceDetail doesn't have technicalName directly - it comes from declaration
    technicalName: '',
  };
}

export function getSegregationGroupCodes(substance: Nullish<ImdgSubstanceDetail>): string[] {
  if (!substance?.segregationGroups?.length) return [];
  return substance.segregationGroups
    .map((group) => group.split(' ')[0])
    .filter((code) => code.startsWith('SGG'));
}

export function getSegregationGroupString(substance: Nullish<ImdgSubstanceDetail>): string {
  return getSegregationGroupCodes(substance).sort().join(', ');
}

export function isImdgSubstanceDetail(obj: unknown): obj is ImdgSubstanceDetail {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'substanceId' in obj &&
    ('class' in obj || 'additionalRequirements' in obj)
  );
}
